import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MessageCircle,
  Wand2,
  Trash2,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import LeadFilters from "./leads/LeadFilters";
import BulkActionBar from "./leads/BulkActionBar";
import PaginationFooter from "./leads/PaginationFooter";
import LeadFormDialog from "./leads/LeadFormDialog";

const STATUS_CLASS = {
  Hot: "status-hot",
  Warm: "status-warm",
  Cold: "status-cold",
  "Followup 1": "status-followup",
};

export default function LeadsPage() {
  const { user } = useAuth();
  const [stages, setStages] = useState([]);
  const [activeStage, setActiveStage] = useState(null);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hasilAkhirFilter, setHasilAkhirFilter] = useState("all");
  const [adminFilter, setAdminFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // 0 = all
  const [total, setTotal] = useState(0);

  // dialogs
  const [createOpen, setCreateOpen] = useState(false);

  const isManager = user?.role === "manager" || user?.role === "super_admin" || user?.role === "direktur";

  useEffect(() => {
    (async () => {
      const [{ data: st }, u] = await Promise.all([
        api.get("/stages"),
        isManager ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setStages(st);
      setUsers(u.data || []);
      if (st.length > 0) setActiveStage(st[0].id);
    })();
  }, [isManager]);

  const fetchLeads = async () => {
    if (!activeStage) return;
    setLoading(true);
    try {
      const params = { stage_id: activeStage, page, page_size: pageSize };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      if (hasilAkhirFilter !== "all") params.hasil_akhir_bucket = hasilAkhirFilter;
      if (adminFilter !== "all") params.assigned_to = adminFilter; // "__unassigned" sentinel handled by backend
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/leads", { params });
      setLeads(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line
  }, [activeStage, statusFilter, hasilAkhirFilter, adminFilter, dateFrom, dateTo, page, pageSize]);

  // Reset to first page when filters/stage change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line
  }, [activeStage, statusFilter, hasilAkhirFilter, adminFilter, dateFrom, dateTo, pageSize]);

  const currentStage = stages.find((s) => s.id === activeStage);
  const statusField = currentStage?.fields?.find((f) => f.key === "status");
  const statusOptions = statusField?.options || ["Cold", "Warm", "Hot", "Followup 1"];

  const userMap = useMemo(() => {
    const map = {};
    users.forEach((u) => (map[u.id] = u));
    return map;
  }, [users]);

  const handleQuickStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}`, { data: { status: newStatus } });
      toast.success("Status updated", { description: newStatus });
      fetchLeads();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleQuickField = async (leadId, key, value) => {
    try {
      await api.patch(`/leads/${leadId}`, { data: { [key]: value } });
      toast.success("Tersimpan");
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const handleQuickAssign = async (leadId, userId) => {
    try {
      await api.patch(`/leads/${leadId}`, { assigned_to: userId || null });
      toast.success("Assignment updated");
      fetchLeads();
    } catch (e) {
      toast.error("Failed to assign");
    }
  };

  const handleDelete = async (leadId) => {
    if (!confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleAutoAssign = async () => {
    try {
      const { data } = await api.post("/leads/auto-assign", null, { params: { stage_id: activeStage } });
      toast.success(`Auto-assigned ${data.assigned} leads`);
      fetchLeads();
    } catch (e) {
      toast.error("Auto-assign failed");
    }
  };

  const handleExport = (format = "csv") => {
    const token = localStorage.getItem("access_token");
    const url = `${API_BASE}/leads/export?stage_id=${activeStage}&format=${format}`;
    const ext = format === "xlsx" ? "xlsx" : "csv";
    fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((blob) => {
        const dl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = dl;
        a.download = `leads_${currentStage?.name || "export"}.${ext}`;
        a.click();
        URL.revokeObjectURL(dl);
      })
      .catch(() => toast.error("Export gagal"));
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/leads/import", form, {
        params: { stage_id: activeStage },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} leads`);
      fetchLeads();
    } catch (err) {
      toast.error("Import failed");
    } finally {
      e.target.value = "";
    }
  };

  const normalizePhone = (raw) => {
    let p = (raw || "").toString().replace(/[^\d+]/g, "");
    p = p.replace(/\+/g, "");
    if (!p) return "";
    // Indonesian normalization to full international format (no +, no leading 0)
    if (p.startsWith("620")) p = "62" + p.slice(3);
    else if (p.startsWith("0")) p = "62" + p.slice(1);
    else if (p.startsWith("8")) p = "62" + p;
    else if (!p.startsWith("62")) p = "62" + p;
    return p;
  };

  const openWhatsApp = (lead, customMessage = null) => {
    const raw = lead.data?.no_wa || "";
    const phone = normalizePhone(raw);
    if (!phone || phone.length < 9) {
      toast.error("Nomor WhatsApp tidak valid pada lead ini");
      return;
    }
    const name = lead.data?.nama || lead.data?.nim || "calon mahasiswa";
    let body =
      customMessage ||
      `Halo ${name}, kami dari tim admisi kampus. Terima kasih atas ketertarikan Anda. Apakah ada yang bisa kami bantu terkait pendaftaran?`;
    // Interpolate {placeholders}
    body = body.replace(/\{(\w+)\}/g, (_, key) => {
      const v = lead.data?.[key];
      return v != null && v !== "" ? String(v) : `{${key}}`;
    });
    const text = encodeURIComponent(body);
    window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${text}`, "_blank");
  };

  const adminUsers = users.filter((u) => u.role === "admin");

  // Multi-select
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignTo, setBulkAssignTo] = useState("");
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };
  const applyBulkAssign = async () => {
    if (selected.size === 0) { toast.error("Pilih lead terlebih dahulu"); return; }
    try {
      await api.post("/leads/assign-bulk", {
        lead_ids: Array.from(selected),
        assigned_to: bulkAssignTo === "__unassigned" ? null : bulkAssignTo || null,
      });
      toast.success(`Assigned ${selected.size} leads`);
      setSelected(new Set());
      fetchLeads();
    } catch (e) {
      toast.error("Bulk assign gagal");
    }
  };

  const applyBulkData = async (key, value) => {
    if (selected.size === 0) { toast.error("Pilih lead terlebih dahulu"); return; }
    try {
      const { data } = await api.post("/leads/update-bulk", {
        lead_ids: Array.from(selected),
        data: { [key]: value },
      });
      toast.success(`${data.modified} lead diperbarui`);
      setSelected(new Set());
      fetchLeads();
    } catch (e) {
      toast.error("Bulk update gagal");
    }
  };

  const applyBulkDelete = async () => {
    if (selected.size === 0) { toast.error("Pilih lead terlebih dahulu"); return; }
    if (!confirm(`Hapus ${selected.size} lead terpilih? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      const { data } = await api.post("/leads/delete-bulk", {
        lead_ids: Array.from(selected),
      });
      toast.success(`${data.deleted} lead dihapus`);
      setSelected(new Set());
      fetchLeads();
    } catch (e) {
      toast.error("Hapus massal gagal");
    }
  };

  const statusBerkasField = (currentStage?.fields || []).find((f) => f.key === "status_berkas");
  const hasilAkhirField = (currentStage?.fields || []).find((f) => f.key === "hasil_akhir");

  // Fields to display: respect show_in_table flag if set, else show all
  const tableFields = (currentStage?.fields || []).filter((f) => f.show_in_table !== false);
  useEffect(() => { setSelected(new Set()); }, [activeStage]);

  // Inline catatan editor
  const [noteEditing, setNoteEditing] = useState(null);
  const [noteText, setNoteText] = useState("");
  const canEditNotes = (lead) =>
    user?.role === "super_admin" || user?.role === "direktur" || (user?.role === "admin" && lead.assigned_to === user.id);
  const openNoteEditor = (lead) => {
    setNoteEditing(lead);
    setNoteText(lead.data?.catatan || "");
  };
  const saveNote = async () => {
    try {
      await api.patch(`/leads/${noteEditing.id}`, { data: { catatan: noteText } });
      toast.success("Catatan tersimpan");
      setNoteEditing(null);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };


  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Pipeline</div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">
            Leads management
          </h1>
        </div>
        {isManager && (
          <div className="flex gap-2">
            <Button
              data-testid="auto-assign-button"
              onClick={handleAutoAssign}
              variant="outline"
              className="border-border bg-transparent hover:bg-[#1C1C1E]"
            >
              <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Auto-assign
            </Button>
            <LeadFormDialog
              open={createOpen}
              setOpen={setCreateOpen}
              stage={currentStage}
              users={adminUsers}
              onCreated={fetchLeads}
            />
          </div>
        )}
      </div>

      <Tabs value={activeStage || ""} onValueChange={setActiveStage}>
        <TabsList className="bg-transparent border-b border-border w-full justify-start rounded-none h-auto p-0 mb-4">
          {stages.map((s) => (
            <TabsTrigger
              key={s.id}
              value={s.id}
              data-testid={`stage-tab-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:font-semibold data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none px-4 py-3 text-foreground/60 hover:text-foreground font-medium transition-colors"
            >
              {s.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Filters */}
        <LeadFilters
          q={q} setQ={setQ}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter} statusOptions={statusOptions}
          hasilAkhirFilter={hasilAkhirFilter} setHasilAkhirFilter={setHasilAkhirFilter}
          adminFilter={adminFilter} setAdminFilter={setAdminFilter} adminUsers={adminUsers}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          isManager={isManager}
          total={total}
          onApply={fetchLeads}
          onReset={() => {
            setQ("");
            setStatusFilter("all");
            setHasilAkhirFilter("all");
            setAdminFilter("all");
            setDateFrom("");
            setDateTo("");
          }}
          onExport={handleExport}
          onImport={handleImport}
        />

        {stages.map((s) => (
          <TabsContent key={s.id} value={s.id} className="mt-0">
            <Card className="bg-[#121212] border-border rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leads-table">
                  <thead className="bg-[#0A0A0A] border-b border-border">
                    <tr>
                      {isManager && (
                        <th className="px-3 py-3 w-8">
                          <input
                            type="checkbox"
                            data-testid="select-all-checkbox"
                            checked={selected.size > 0 && selected.size === leads.length}
                            onChange={toggleSelectAll}
                            className="accent-primary"
                          />
                        </th>
                      )}
                      {tableFields.map((f) => (
                        <th key={f.key} className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-left px-4 py-3">
                          {f.label}
                        </th>
                      ))}
                      {isManager && (
                        <th className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-left px-4 py-3">Admin</th>
                      )}
                      <th className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={10} className="text-center py-12 text-muted-foreground text-sm">Loading...</td></tr>
                    ) : leads.length === 0 ? (
                      <tr><td colSpan={10} className="text-center py-12 text-muted-foreground text-sm">No leads found. Adjust filters or add a new lead.</td></tr>
                    ) : (
                      leads.map((lead) => {
                        const status = lead.data?.status;
                        return (
                          <tr key={lead.id} className="border-b border-border hover:bg-[#1C1C1E] transition-colors" data-testid={`lead-row-${lead.id}`}>
                            {isManager && (
                              <td className="px-3 py-3">
                                <input
                                  type="checkbox"
                                  data-testid={`select-${lead.id}`}
                                  checked={selected.has(lead.id)}
                                  onChange={() => toggleSelect(lead.id)}
                                  className="accent-primary"
                                />
                              </td>
                            )}
                            {tableFields.map((f) => {
                              const v = lead.data?.[f.key];
                              if (f.key === "status") {
                                return (
                                  <td key={f.key} className="px-4 py-3">
                                    {statusField ? (
                                      <Select value={status || ""} onValueChange={(val) => handleQuickStatus(lead.id, val)}>
                                        <SelectTrigger
                                          data-testid={`status-select-${lead.id}`}
                                          className={`h-7 text-xs font-mono w-32 border ${status ? STATUS_CLASS[status] || "border-border" : "border-border bg-transparent"}`}
                                        >
                                          <SelectValue placeholder="—" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {statusOptions.map((opt) => (
                                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </td>
                                );
                              }
                              // Generic inline editable select (covers hasil_akhir, sumber_data, status_berkas, status_pembayaran, etc.)
                              if (f.type === "select" && canEditNotes(lead)) {
                                const opts = f.options && f.options.length > 0 ? f.options : [];
                                return (
                                  <td key={f.key} className="px-4 py-3">
                                    <Select value={v || ""} onValueChange={(val) => handleQuickField(lead.id, f.key, val)}>
                                      <SelectTrigger
                                        data-testid={`${f.key}-select-${lead.id}`}
                                        className="h-7 text-xs w-36 bg-transparent border-border"
                                      >
                                        <SelectValue placeholder="—" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {opts.map((opt) => (
                                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                );
                              }
                              return (
                                <td key={f.key} className="px-4 py-3 text-sm text-white">
                                  {f.key === "catatan" ? (
                                    <button
                                      data-testid={`edit-note-${lead.id}`}
                                      onClick={() => canEditNotes(lead) && openNoteEditor(lead)}
                                      disabled={!canEditNotes(lead)}
                                      className={`text-left text-xs max-w-[200px] truncate ${canEditNotes(lead) ? "hover:text-primary cursor-pointer" : "text-muted-foreground cursor-default"}`}
                                      title={canEditNotes(lead) ? "Klik untuk edit catatan" : v || "—"}
                                    >
                                      {v ? <span className="line-clamp-1">{v}</span> : <span className="text-muted-foreground italic">{canEditNotes(lead) ? "Tambah catatan…" : "—"}</span>}
                                    </button>
                                  ) : f.key === "no_wa" && v ? (
                                    <span className="font-mono text-xs">{v}</span>
                                  ) : f.key === "nominal_tagihan" && v ? (
                                    <span className="font-mono text-xs">Rp {Number(v).toLocaleString("id-ID")}</span>
                                  ) : (
                                    <span className={f.key === "nac" || f.key === "nim" ? "font-mono text-xs text-muted-foreground" : ""}>
                                      {v || "—"}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            {isManager && (
                              <td className="px-4 py-3">
                                <Select
                                  value={lead.assigned_to || "__unassigned"}
                                  onValueChange={(v) => handleQuickAssign(lead.id, v === "__unassigned" ? "" : v)}
                                >
                                  <SelectTrigger data-testid={`assign-select-${lead.id}`} className="h-7 text-xs w-36 bg-transparent border-border">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__unassigned">Unassigned</SelectItem>
                                    {adminUsers.map((a) => (
                                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                            )}
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {(currentStage?.wa_templates || []).length > 0 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        data-testid={`whatsapp-quick-action-${lead.id}`}
                                        className="p-1.5 rounded-md bg-[#32D74B]/10 hover:bg-[#32D74B]/20 text-[#32D74B] border border-[#32D74B]/20 transition-all"
                                        title="WhatsApp templates"
                                      >
                                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="bg-[#121212] border-border w-72 p-2" align="end">
                                      <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground px-2 py-1.5">
                                        Pilih template
                                      </div>
                                      <div className="space-y-1">
                                        {(currentStage.wa_templates || []).map((t, i) => (
                                          <button
                                            key={i}
                                            data-testid={`wa-template-${lead.id}-${i}`}
                                            onClick={() => openWhatsApp(lead, t.message)}
                                            className="w-full text-left px-3 py-2 rounded-md hover:bg-[#1C1C1E] transition-colors group"
                                          >
                                            <div className="text-sm text-white font-medium">{t.name}</div>
                                            <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{t.message}</div>
                                          </button>
                                        ))}
                                        <button
                                          onClick={() => openWhatsApp(lead)}
                                          className="w-full text-left px-3 py-2 rounded-md hover:bg-[#1C1C1E] transition-colors border-t border-border mt-1 pt-2"
                                        >
                                          <div className="text-xs text-muted-foreground">Pesan default</div>
                                        </button>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <button
                                    data-testid={`whatsapp-quick-action-${lead.id}`}
                                    onClick={() => openWhatsApp(lead)}
                                    className="p-1.5 rounded-md bg-[#32D74B]/10 hover:bg-[#32D74B]/20 text-[#32D74B] border border-[#32D74B]/20 transition-all"
                                    title="Send WhatsApp"
                                  >
                                    <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {isManager && (
                                  <button
                                    onClick={() => handleDelete(lead.id)}
                                    className="p-1.5 rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/20 transition-all"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationFooter
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={total}
              />
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {isManager && selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          adminUsers={adminUsers}
          bulkAssignTo={bulkAssignTo}
          setBulkAssignTo={setBulkAssignTo}
          applyBulkAssign={applyBulkAssign}
          statusBerkasField={statusBerkasField}
          hasilAkhirField={hasilAkhirField}
          applyBulkData={applyBulkData}
          applyBulkDelete={applyBulkDelete}
          onClear={() => setSelected(new Set())}
        />
      )}

      {/* Note editor dialog */}
      <Dialog open={!!noteEditing} onOpenChange={(o) => !o && setNoteEditing(null)}>
        <DialogContent className="bg-[#121212] border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Catatan — {noteEditing?.data?.nama || noteEditing?.data?.nim || "Lead"}
            </DialogTitle>
          </DialogHeader>
          <textarea
            data-testid="note-textarea"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={6}
            placeholder="Tulis catatan follow-up..."
            className="w-full bg-[#0A0A0A] border border-border rounded-md p-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteEditing(null)} className="border-border bg-transparent">
              Cancel
            </Button>
            <Button data-testid="save-note-button" onClick={saveNote} className="bg-primary hover:bg-[#0056B3]">
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
